import { onPageIn, onMount, onDestroy } from '@/modules/_'
import Swiper from 'swiper'
import { Navigation, Autoplay, EffectFade } from 'swiper/modules'
import gsap from 'gsap'
import { Scroll } from '@/lib/scroll'

export default function (element: HTMLElement, dataset: DOMStringMap) {
  let swiper: Swiper | null = null
  let cursorElement: HTMLDivElement | null = null
  let isAnimating = false
  const AUTOPLAY_DELAY = 5000
  const TRANSITION_DURATION = 800

  // Smooth cursor variables
  let isHoveringSlider = false
  let animationFrame: number | null = null
  let mouseX = 0
  let mouseY = 0
  let currentX = 0
  let currentY = 0

  // Touch screen detection
  const isTouchDevice = () => {
    return (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      (navigator as any).msMaxTouchPoints > 0
    )
  }

  // Section configuration - dynamically built from slides
  const sectionConfig: Map<number, { start: number; end: number }> = new Map()

  const lerp = (start: number, end: number, factor: number) => {
    return start + (end - start) * factor
  }

  onMount(() => {
    initializeSections()
    initializeSwiper()
    initializeCustomCursor()
    initializeTriggers()
    initializePaginationItems()
    initializeCloseButton()
  })

  onDestroy(() => {
    cleanup()
  })

  function initializeSections() {
    const slides = element.querySelectorAll<HTMLElement>(
      '.citation_slider_slide'
    )
    const sections: Map<number, number[]> = new Map()

    // Group slides by section
    slides.forEach((slide, index) => {
      const section = parseInt(slide.dataset.citationSection || '0')
      if (!sections.has(section)) {
        sections.set(section, [])
      }
      sections.get(section)!.push(index)
    })

    // Create section config with start and end indices
    sections.forEach((slideIndices, sectionNum) => {
      slideIndices.sort((a, b) => a - b)
      sectionConfig.set(sectionNum, {
        start: slideIndices[0],
        end: slideIndices[slideIndices.length - 1],
      })
    })
  }

  function initializeSwiper() {
    const wrapper = element.querySelector('.citation_slider_wrapper')
    if (!wrapper) return

    swiper = new Swiper(element, {
      modules: [Navigation, Autoplay, EffectFade],
      effect: 'fade',
      fadeEffect: {
        crossFade: true,
      },
      speed: 600,
      allowTouchMove: false,
      autoplay: false,
      on: {
        slideChangeTransitionStart: () => {
          isAnimating = true
          handleSlideTransition('out')
        },
        slideChangeTransitionEnd: () => {
          isAnimating = false
          handleSlideTransition('in')
          updateCitationActiveStates()
          // updatePaginationVisibility()
          scheduleNextSlide()
        },
      },
    })
  }

  function scheduleNextSlide() {
    if (!swiper || !element.classList.contains('is--active')) return

    const currentSection = getCurrentSection()
    if (!currentSection) return

    // Clear any existing timeout
    if ((window as any).__swiperAutoplayTimeout) {
      clearTimeout((window as any).__swiperAutoplayTimeout)
    }

    // Schedule next slide
    ;(window as any).__swiperAutoplayTimeout = setTimeout(() => {
      if (!swiper || !element.classList.contains('is--active')) return

      const currentSection = getCurrentSection()
      if (!currentSection) return

      // If we're at the last slide of the section, loop back to the first
      if (swiper.activeIndex >= currentSection.end) {
        swiper.slideTo(currentSection.start)
      } else {
        swiper.slideNext()
      }
    }, AUTOPLAY_DELAY)
  }

  function updatePaginationVisibility() {
    if (!swiper) return

    const currentSection = getCurrentSection()
    if (!currentSection) {
      return
    }

    // Hide all pagination items first
    const allPaginationItems = element.querySelectorAll<HTMLElement>(
      '.citation_wrap[data-citation-item=""]'
    )
    allPaginationItems.forEach((item) => {
      item.style.display = 'none'
    })

    // Show only pagination items for the current section
    const currentSectionPagination = element.querySelectorAll<HTMLElement>(
      `.citation_wrap[data-citation-item=""][data-section="${currentSection.sectionNum}"]`
    )

    currentSectionPagination.forEach((item) => {
      item.style.display = ''
    })
  }

  function animateCursor() {
    if (!cursorElement || !isHoveringSlider) return

    currentX = lerp(currentX, mouseX, 0.15)
    currentY = lerp(currentY, mouseY, 0.15)

    cursorElement.style.left = `${currentX}px`
    cursorElement.style.top = `${currentY}px`

    animationFrame = requestAnimationFrame(animateCursor)
  }

  function initializeCustomCursor() {
    // Don't initialize custom cursor on touch devices
    if (isTouchDevice()) {
      return
    }

    cursorElement = document.createElement('div')
    cursorElement.className = 'citation-custom-cursor'
    cursorElement.style.cssText = `
      position: fixed;
      pointer-events: none;
      z-index: 10000;
      display: block;
      font-family: var(--_typography---font--primary-family);
      font-size: 12px;
      font-weight: var(--_typography---font--primary-medium);
      color: var(--swatch--tan-0);
      text-transform: uppercase;
      background: var(--swatch--tan-7);
      padding: 0.5rem 1rem;
      border-radius: 50px;
      transform: translate(-50%, -50%);
      opacity: 0;
      transition: opacity 0.3s ease;
    `
    document.body.appendChild(cursorElement)

    // Mouse move handler
    const handleMouseMove = (e: MouseEvent) => {
      mouseX = e.clientX
      mouseY = e.clientY

      if (!element.classList.contains('is--active') || !cursorElement) return

      // Check if hovering over excluded elements
      const target = e.target as HTMLElement
      const isExcluded =
        target.closest('.change_moment_citation_wrap') ||
        target.closest('.citation_slide_close_wrap') ||
        target.closest('.citation_caestudy_wrap')

      if (isExcluded) {
        document.body.style.cursor = 'pointer'
        cursorElement.style.opacity = '0'
        isHoveringSlider = false
        if (animationFrame) {
          cancelAnimationFrame(animationFrame)
          animationFrame = null
        }
        return
      } else {
        document.body.style.cursor = 'none'

        if (!isHoveringSlider) {
          isHoveringSlider = true
          currentX = mouseX
          currentY = mouseY
          cursorElement.style.left = `${currentX}px`
          cursorElement.style.top = `${currentY}px`
          animateCursor()
        }

        cursorElement.style.opacity = '1'
      }

      if (isHoveringSlider && !animationFrame) {
        animateCursor()
      }

      updateCursorText(e.clientX)
    }

    const handleMouseLeave = () => {
      if (cursorElement) {
        cursorElement.style.opacity = '0'
      }
      document.body.style.cursor = ''
      isHoveringSlider = false
      if (animationFrame) {
        cancelAnimationFrame(animationFrame)
        animationFrame = null
      }
    }

    element.addEventListener('mousemove', handleMouseMove)
    element.addEventListener('mouseleave', handleMouseLeave)

    // Click handler for navigation
    element.addEventListener('click', (e: MouseEvent) => {
      if (isAnimating || !swiper) return

      const target = e.target as HTMLElement

      // Check if clicking on excluded elements
      const isExcluded =
        target.closest('.change_moment_citation_wrap') ||
        target.closest('.citation_slide_close_wrap') ||
        target.closest('.citation_caestudy_wrap')

      if (isExcluded) return

      const currentSection = getCurrentSection()
      if (!currentSection) return

      const isLastSlide = swiper.activeIndex === currentSection.end
      const screenMidpoint = window.innerWidth / 2

      if (isLastSlide && e.clientX > screenMidpoint) {
        closeSlider()
      } else if (e.clientX < screenMidpoint) {
        navigatePrevious()
      } else {
        navigateNext()
      }
    })
  }

  function updateCursorText(mouseX: number) {
    if (!cursorElement || !swiper) return

    const currentSection = getCurrentSection()
    if (!currentSection) return

    const isLastSlide = swiper.activeIndex === currentSection.end
    const screenMidpoint = window.innerWidth / 2

    if (isLastSlide && mouseX > screenMidpoint) {
      cursorElement.textContent = 'Close'
    } else if (mouseX < screenMidpoint) {
      cursorElement.textContent = 'Prev'
    } else {
      cursorElement.textContent = 'Next'
    }
  }

  function getCurrentSection() {
    if (!swiper) return null

    const currentIndex = swiper.activeIndex

    // Get the section directly from the active slide
    const activeSlide = element.querySelector<HTMLElement>(
      '.swiper-slide-active'
    )
    if (activeSlide) {
      const sectionNum = parseInt(activeSlide.dataset.citationSection || '0')
      const config = sectionConfig.get(sectionNum)

      if (config) {
        return { ...config, sectionNum }
      }
    }

    // Fallback: search through all sections
    for (const [sectionNum, config] of sectionConfig.entries()) {
      if (currentIndex >= config.start && currentIndex <= config.end) {
        return { ...config, sectionNum }
      }
    }
    return null
  }

  function navigateNext() {
    if (!swiper) return

    const currentSection = getCurrentSection()
    if (!currentSection) return

    if (swiper.activeIndex < currentSection.end) {
      swiper.slideNext()
    } else {
      // Loop back to the start of the section
      swiper.slideTo(currentSection.start)
    }
  }

  function navigatePrevious() {
    if (!swiper) return

    const currentSection = getCurrentSection()
    if (!currentSection) return

    if (swiper.activeIndex > currentSection.start) {
      swiper.slidePrev()
    } else {
      // Loop to the end of the section
      swiper.slideTo(currentSection.end)
    }
  }

  function handleSlideTransition(direction: 'in' | 'out') {
    const activeSlide = element.querySelector('.swiper-slide-active')
    if (!activeSlide) return

    const img = activeSlide.querySelector('.citation_slide_caestudy_img')
    const title = activeSlide.querySelector('.citation_caestudy_content_title')
    const body = activeSlide.querySelector('.citation_caestudy_content_body')
    const cta = activeSlide.querySelector('.button_link_wrap')

    const elements = [img, title, body, cta].filter(Boolean)

    const transitionTL = gsap.timeline({ paused: true })

    transitionTL
      .to(img, {
        y: '100%',
        opacity: 0,
      })
      .to(title, {
        y: '100%',
        opacity: 0,
      })
      .to(body, {
        y: '100%',
        opacity: 0,
      })
      .to(cta, {
        y: '100%',
        opacity: 0,
      })

    // if (direction === 'out') {
    //   transitionTL.play()
    // } else {
    //   transitionTL.reverse()
    // }
  }

  // Initialize trigger items (outside the slider - open the slider)
  function initializeTriggers() {
    const triggers = document.querySelectorAll<HTMLElement>(
      '[data-citation-item="trigger"]'
    )

    triggers.forEach((trigger) => {
      const section = parseInt(trigger.dataset.section || '0')
      const slideIndex = parseInt(trigger.dataset.slide || '0')

      // Only add hover events on non-touch devices
      if (!isTouchDevice()) {
        trigger.addEventListener('mouseenter', (e) => {
          e.stopPropagation()
          previewSlider(section, slideIndex)
        })
        trigger.addEventListener('mouseleave', (e) => {
          e.stopPropagation()
          closePreview()
        })
      }

      trigger.addEventListener('click', (e) => {
        e.stopPropagation()

        if (isAnimating) return
        openSlider(section, slideIndex)
      })
    })
  }

  function initializePaginationItems() {
    // Find all pagination items inside the slider
    const paginationItems = element.querySelectorAll<HTMLElement>(
      '.citation_wrap[data-citation-item=""]'
    )

    paginationItems.forEach((item) => {
      item.addEventListener('click', (e) => {
        e.stopPropagation()

        const section = parseInt(item.dataset.section || '0')
        const slideIndex = parseInt(item.dataset.slide || '0')

        const currentSection = getCurrentSection()

        // Only navigate if clicking within the current section
        if (currentSection && section === currentSection.sectionNum) {
          navigateToSlide(section, slideIndex)
        } else {
        }
      })
    })
  }

  function initializeCloseButton() {
    const closeButton = element.querySelector('.citation_slide_close_wrap')
    if (closeButton) {
      closeButton.addEventListener('click', (e) => {
        e.stopPropagation()
        closeSlider()
      })
    }
    // ESC key to close
    const handleEscKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && element.classList.contains('is--active')) {
        closeSlider()
      }
    }

    document.addEventListener('keydown', handleEscKey)
  }

  function navigateToSlide(section: number, slideIndex: number) {
    if (!swiper) return

    // Find the actual slide index
    const slides = element.querySelectorAll<HTMLElement>(
      '.citation_slider_slide'
    )
    let targetIndex = -1

    slides.forEach((slide, index) => {
      const slideSection = parseInt(slide.dataset.citationSection || '0')
      const slideNumber = parseInt(slide.dataset.slideIndex || '0')

      if (slideSection === section && slideNumber === slideIndex) {
        targetIndex = index
      }
    })

    if (targetIndex !== -1 && targetIndex !== swiper.activeIndex) {
      swiper.slideTo(targetIndex)
    } else if (targetIndex === swiper.activeIndex) {
    } else {
    }
  }

  const closePreview = () => {
    const previewWrapper = document.querySelector(
      '[data-citation-preview]'
    ) as HTMLElement
    const previewImage = document.querySelector(
      '[data-citation-preview="image"]'
    ) as HTMLElement

    previewImage.classList.remove('is--active')
    previewWrapper.classList.remove('is--open')
  }

  const previewSlider = (section: number, slideIndex: number) => {
    const slides = element.querySelectorAll<HTMLElement>(
      '.citation_slider_slide'
    )
    let bg = ''
    let image = ''

    slides.forEach((slide) => {
      const slideSection = parseInt(slide.dataset.citationSection || '0')
      const slideNumber = parseInt(slide.dataset.slideIndex || '0')

      if (slideSection === section && slideNumber === slideIndex) {
        bg = slide.querySelector('[data-citation-slide-bg] img')?.src || ''
        image =
          slide.querySelector('[data-citation-slide-image] img')?.src || ''
      }
    })

    const previewWrapper = document.querySelector(
      '[data-citation-preview]'
    ) as HTMLElement
    const previewImage = document.querySelector(
      '[data-citation-preview="image"]'
    ) as HTMLElement
    const previewBg = document.querySelector(
      '[data-citation-preview="bg"]'
    ) as HTMLElement

    // Safe assignment with null checks
    const bgImg = previewBg.querySelector('img')
    const previewImg = previewImage.querySelector('img')

    if (bgImg && bg) bgImg.src = bg
    if (previewImg && image) previewImg.src = image

    previewWrapper.classList.add('is--open')
    previewImage.classList.add('is--active')
  }

  function openSlider(section: number, slideIndex: number) {
    if (!swiper) return
    // Find the actual slide index
    const slides = element.querySelectorAll<HTMLElement>(
      '.citation_slider_slide'
    )
    let targetIndex = 0

    slides.forEach((slide, index) => {
      const slideSection = parseInt(slide.dataset.citationSection || '0')
      const slideNumber = parseInt(slide.dataset.slideIndex || '0')

      if (slideSection === section && slideNumber === slideIndex) {
        targetIndex = index
      }
    })

    element.style.opacity = '0'
    element.style.display = 'flex'
    element.style.backgroundColor = 'white'
    element.classList.add('is--active')
    element.style.transition = '0.6s ease-out'

    Scroll.stop()

    // Show slider
    setTimeout(() => {
      element.style.opacity = '1'
    }, 400)

    // Go to slide
    swiper.slideTo(targetIndex, 0)

    // Show close button with animation
    const closeButton = element.querySelector('.citation_slide_close_wrap')
    if (closeButton) {
      setTimeout(() => {
        closeButton.classList.add('is--active')
      }, 300)
    }

    // Trigger slide-in animation
    setTimeout(() => {
      handleSlideTransition('in')
    }, 100)

    updateCitationActiveStates()
    // updatePaginationVisibility()

    // Start autoplay
    // scheduleNextSlide()
  }

  function closeSlider() {
    if (!swiper) return

    // Clear autoplay timeout
    if ((window as any).__swiperAutoplayTimeout) {
      clearTimeout((window as any).__swiperAutoplayTimeout)
    }

    // Hide close button
    const closeButton = element.querySelector('.citation_slide_close_wrap')
    if (closeButton) {
      closeButton.classList.remove('is--active')
    }

    // Fade out
    element.style.opacity = '0'

    setTimeout(() => {
      element.classList.remove('is--active')
      element.style.display = 'none'
      element.style.opacity = '1'

      // Reset all citation active states
      document
        .querySelectorAll('[data-citation-item].is--active')
        .forEach((el) => {
          el.classList.remove('is--active')
        })

      // Reset all pagination visibility
      const allPaginationItems = element.querySelectorAll<HTMLElement>(
        '.citation_wrap[data-citation-item=""]'
      )
      allPaginationItems.forEach((item) => {
        item.style.display = ''
      })
    }, 400)

    Scroll.start()

    gsap.to('.change_design_wrap .change_moment_wrap', {
      opacity: 1,
      duration: 0.6,
      stagger: {
        each: 0.1,
        from: 'end',
      },
      ease: 'cubic-bezier(0.45, 0, 0.55, 1)',
    })
  }

  function updateCitationActiveStates() {
    if (!swiper) return

    const activeSlide = element.querySelector<HTMLElement>(
      '.swiper-slide-active'
    )
    if (!activeSlide) return

    const section = parseInt(activeSlide.dataset.citationSection || '0')
    const slideIndex = parseInt(activeSlide.dataset.slideIndex || '0')

    // Remove all active states from ALL citation items (both triggers and pagination)
    document.querySelectorAll('[data-citation-item]').forEach((el) => {
      el.classList.remove('is--active')
    })

    // Add active state to matching citation items
    const activeCitations = document.querySelectorAll(
      `[data-citation-item][data-section="${section}"][data-slide="${slideIndex}"]`
    )
    activeCitations.forEach((citation) => {
      citation.classList.add('is--active')
    })
  }

  function cleanup() {
    // Clear autoplay timeout
    if ((window as any).__swiperAutoplayTimeout) {
      clearTimeout((window as any).__swiperAutoplayTimeout)
    }

    // Clear animation frame
    if (animationFrame) {
      cancelAnimationFrame(animationFrame)
      animationFrame = null
    }

    if (swiper) {
      swiper.destroy()
      swiper = null
    }

    if (cursorElement) {
      cursorElement.remove()
      cursorElement = null
    }

    document.body.style.cursor = ''
  }
}
